import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import API from '../services/api';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

const emptyForm = {
  id: '',
  scope: 'default',
  contractorUser: '',
  workType: '',
  unit: 'job',
  rate: '',
  active: true,
};

const AdminContractorRates = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [rates, setRates] = useState([]);
  const [entries, setEntries] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [contractors, setContractors] = useState([]);
  const [form, setForm] = useState(emptyForm);

  const [filters, setFilters] = useState({
    contractorId: '',
    from: '',
    to: '',
    workType: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [rRes, eRes, cRes] = await Promise.all([
        API.get('/admin/contractor-work-rates'),
        API.get('/admin/contractor-work-entries', { params: filters }),
        API.get('/admin/contractors'),
      ]);
      setRates(Array.isArray(rRes.data) ? rRes.data : []);
      setEntries(Array.isArray(eRes.data?.entries) ? eRes.data.entries : []);
      setTotalAmount(Number(eRes.data?.totalAmount || 0));
      setContractors(Array.isArray(cRes.data) ? cRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load contractor rate data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeForm = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      if (name === 'scope') {
        return {
          ...prev,
          scope: value,
          contractorUser: value === 'contractor' ? prev.contractorUser : '',
        };
      }
      return { ...prev, [name]: type === 'checkbox' ? checked : value };
    });
  };

  const onChangeFilters = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const saveRate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        workType: form.workType,
        unit: form.unit,
        rate: Number(form.rate),
        active: !!form.active,
        contractorUser: form.scope === 'contractor' ? form.contractorUser : '',
      };

      if (form.scope === 'contractor' && !form.contractorUser) {
        throw new Error('Select contractor for contractor-specific rate');
      }

      if (form.id) {
        await API.put(`/admin/contractor-work-rates/${form.id}`, { ...payload, scope: form.scope });
        setMessage('Rate updated successfully');
      } else {
        await API.post('/admin/contractor-work-rates', payload);
        setMessage('Rate saved successfully');
      }

      resetForm();
      await load();
    } catch (e1) {
      setError(e1?.response?.data?.message || e1.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setMessage('');
    setError('');
    setForm({
      id: row._id,
      scope: row.scope || 'default',
      contractorUser: row.scope === 'contractor' ? (row.contractorUser?._id || row.contractorUser || '') : '',
      workType: row.workType || '',
      unit: row.unit || 'job',
      rate: String(Number(row.rate || 0)),
      active: !!row.active,
    });
  };

  const toggleActive = async (row) => {
    try {
      await API.put(`/admin/contractor-work-rates/${row._id}`, {
        active: !row.active,
        scope: row.scope || 'default',
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update rate');
    }
  };

  const removeRate = async (row) => {
    if (!window.confirm('Delete this rate?')) return;
    try {
      await API.delete(`/admin/contractor-work-rates/${row._id}`, {
        data: { scope: row.scope || 'default' },
      });
      if (form.id === row._id) resetForm();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete rate');
    }
  };

  const contractorOptions = useMemo(
    () => contractors.map((item) => ({
      id: item._id,
      name: item.profile?.contractorName || item.fullName || 'Contractor',
    })),
    [contractors]
  );

  const uniqueContractors = useMemo(() => {
    const map = new Map();
    contractors.forEach((c) => {
      map.set(c._id, c.profile?.contractorName || c.fullName || 'Contractor');
    });
    entries.forEach((e) => {
      const id = e?.contractorUser?._id;
      if (!id || map.has(id)) return;
      map.set(id, e.contractorUser.fullName || id);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [contractors, entries]);

  return (
    <div className="d-grid gap-3">
      <Card>
        <Card.Body>
          <h4 className="mb-3">Contractor Work Price Setup</h4>
          {message && <Alert variant="success">{message}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={saveRate}>
            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Label>Rate Scope</Form.Label>
                <Form.Select name="scope" value={form.scope} onChange={onChangeForm}>
                  <option value="default">Default for All Contractors</option>
                  <option value="contractor">Specific Contractor</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Contractor</Form.Label>
                <Form.Select
                  name="contractorUser"
                  value={form.contractorUser}
                  onChange={onChangeForm}
                  disabled={form.scope !== 'contractor'}
                  required={form.scope === 'contractor'}
                >
                  <option value="">Select Contractor</option>
                  {contractorOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Work Type</Form.Label>
                <Form.Control name="workType" value={form.workType} onChange={onChangeForm} required />
              </Col>
              <Col md={3}>
                <Form.Label>Unit</Form.Label>
                <Form.Control name="unit" value={form.unit} onChange={onChangeForm} required />
              </Col>
              <Col md={3}>
                <Form.Label>Rate</Form.Label>
                <Form.Control type="number" min="0" step="0.01" name="rate" value={form.rate} onChange={onChangeForm} required />
              </Col>
              <Col md={2}>
                <Form.Check type="checkbox" name="active" checked={form.active} onChange={onChangeForm} label="Active" />
              </Col>
              <Col md={7} className="d-flex gap-2">
                <Button type="submit" disabled={saving}>{saving ? 'Saving...' : form.id ? 'Update Rate' : 'Save Rate'}</Button>
                {form.id ? (
                  <Button type="button" variant="outline-secondary" onClick={resetForm}>Cancel Edit</Button>
                ) : null}
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <Table responsive hover className="mt-3">
              <thead>
                <tr>
                  <th>Scope</th>
                  <th>Contractor</th>
                  <th>Work Type</th>
                  <th>Unit</th>
                  <th>Rate</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.length === 0 ? (
                  <tr><td colSpan="7" className="text-center">No rates configured</td></tr>
                ) : (
                  rates.map((r) => (
                    <tr key={`${r.scope || 'default'}-${r._id}`}>
                      <td>
                        <Badge bg={r.scope === 'contractor' ? 'info' : 'secondary'}>
                          {r.scope === 'contractor' ? 'Contractor Specific' : 'Default'}
                        </Badge>
                      </td>
                      <td>{r.contractorName || r.contractorUser?.fullName || 'All Contractors'}</td>
                      <td>{r.workType}</td>
                      <td>{r.unit || 'job'}</td>
                      <td>₹{Number(r.rate || 0).toLocaleString('en-IN')}</td>
                      <td><Badge bg={r.active ? 'success' : 'secondary'}>{r.active ? 'Active' : 'Inactive'}</Badge></td>
                      <td className="d-flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline-dark" onClick={() => startEdit(r)}>Edit</Button>
                        <Button size="sm" variant="outline-primary" onClick={() => toggleActive(r)}>
                          {r.active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => removeRate(r)}>Delete</Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Contractor Work Entries</h5>
            <div className="text-muted">Total Amount: ₹{totalAmount.toLocaleString('en-IN')}</div>
          </div>

          <Row className="g-2 mb-3">
            <Col md={3}>
              <Form.Select name="contractorId" value={filters.contractorId} onChange={onChangeFilters}>
                <option value="">All Contractors</option>
                {uniqueContractors.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}><Form.Control type="date" name="from" value={filters.from} onChange={onChangeFilters} /></Col>
            <Col md={2}><Form.Control type="date" name="to" value={filters.to} onChange={onChangeFilters} /></Col>
            <Col md={3}><Form.Control placeholder="Work type" name="workType" value={filters.workType} onChange={onChangeFilters} /></Col>
            <Col md={2}><Button className="w-100" onClick={load}>Apply</Button></Col>
          </Row>

          <Table responsive hover>
            <thead>
              <tr>
                <th>Date</th>
                <th>Contractor</th>
                <th>Worker</th>
                <th>Work Type</th>
                <th>Qty</th>
                <th>Bag Details</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan="8" className="text-center">No entries found</td></tr>
              ) : (
                entries.map((e) => (
                  <tr key={e._id}>
                    <td>{fmtDate(e.workDate)}</td>
                    <td>{e.contractorUser?.fullName || '—'}</td>
                    <td>{e.workerName}</td>
                    <td>{e.workType}</td>
                    <td>{e.quantity} {e.unit || ''}</td>
                    <td>{Array.isArray(e.bagDetails) && e.bagDetails.length ? e.bagDetails.map((row) => `${row.bagSize}`).join(', ') : (e.unit || '—')}</td>
                    <td>₹{Number(e.unitRate || 0).toLocaleString('en-IN')}</td>
                    <td>₹{Number(e.totalAmount || 0).toLocaleString('en-IN')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminContractorRates;
